// Mobile Performance Optimization Utilities
// ==========================================

// Lazy loading utility
export const lazyLoad = (callback, options = {}) => {
  const defaultOptions = {
    root: null,
    rootMargin: '50px',
    threshold: 0.1
  };

  const observerOptions = { ...defaultOptions, ...options };

  return new Promise((resolve) => {
    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            callback();
            observer.unobserve(entry.target);
            resolve();
          }
        });
      }, observerOptions);

      return observer;
    } else {
      // Fallback for older browsers
      callback();
      resolve();
    }
  });
};

// Image lazy loading with placeholder
export const createLazyImage = (src, alt, placeholder = '') => {
  const img = document.createElement('img');
  img.alt = alt;
  img.loading = 'lazy';
  
  // Set placeholder while loading
  if (placeholder) {
    img.src = placeholder;
  }
  
  // Use Intersection Observer for lazy loading
  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        img.src = src;
        observer.unobserve(img);
      }
    });
  });
  
  observer.observe(img);
  return img;
};

// Debounce utility for performance
export const debounce = (func, wait, immediate = false) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      timeout = null;
      if (!immediate) func(...args);
    };
    const callNow = immediate && !timeout;
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
    if (callNow) func(...args);
  };
};

// Throttle utility for scroll events
export const throttle = (func, limit) => {
  let inThrottle;
  return function executedFunction(...args) {
    if (!inThrottle) {
      func.apply(this, args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
};

// Virtual scrolling for long lists
export class VirtualScroller {
  constructor(container, itemHeight, renderItem) {
    this.container = container;
    this.itemHeight = itemHeight;
    this.renderItem = renderItem;
    this.visibleItems = new Map();
    this.data = [];
    this.scrollTop = 0;
    this.containerHeight = container.clientHeight;
    
    this.setupScrollListener();
  }

  setData(data) {
    this.data = data;
    this.render();
  }

  setupScrollListener() {
    this.container.addEventListener('scroll', throttle(() => {
      this.scrollTop = this.container.scrollTop;
      this.render();
    }, 16)); // 60fps
  }

  render() {
    const startIndex = Math.floor(this.scrollTop / this.itemHeight);
    const endIndex = Math.min(
      startIndex + Math.ceil(this.containerHeight / this.itemHeight) + 1,
      this.data.length
    );

    // Clear existing items
    this.container.innerHTML = '';

    // Create spacer for items above viewport
    const topSpacer = document.createElement('div');
    topSpacer.style.height = `${startIndex * this.itemHeight}px`;
    this.container.appendChild(topSpacer);

    // Render visible items
    for (let i = startIndex; i < endIndex; i++) {
      const item = this.renderItem(this.data[i], i);
      this.container.appendChild(item);
    }

    // Create spacer for items below viewport
    const bottomSpacer = document.createElement('div');
    bottomSpacer.style.height = `${(this.data.length - endIndex) * this.itemHeight}px`;
    this.container.appendChild(bottomSpacer);
  }
}

// Network-aware loading
export const getNetworkType = () => {
  if ('connection' in navigator) {
    return navigator.connection.effectiveType;
  }
  return 'unknown';
};

export const isSlowNetwork = () => {
  const connection = navigator.connection;
  if (!connection) return false;
  
  return connection.effectiveType === 'slow-2g' || 
         connection.effectiveType === '2g' ||
         connection.saveData === true;
};

// Adaptive loading based on network
export const adaptiveLoad = (slowCallback, fastCallback) => {
  if (isSlowNetwork()) {
    slowCallback();
  } else {
    fastCallback();
  }
};

// Preload critical resources
export const preloadResource = (url, type = 'script') => {
  const link = document.createElement('link');
  link.rel = 'preload';
  link.href = url;
  link.as = type;
  document.head.appendChild(link);
};

// Resource hints for better loading
export const addResourceHints = (resources) => {
  resources.forEach(({ url, type }) => {
    const link = document.createElement('link');
    link.rel = 'prefetch';
    link.href = url;
    if (type) link.as = type;
    document.head.appendChild(link);
  });
};

// Memory management for large datasets
export class MemoryManager {
  constructor(maxSize = 100) {
    this.cache = new Map();
    this.maxSize = maxSize;
    this.accessOrder = [];
  }

  get(key) {
    if (this.cache.has(key)) {
      // Move to end (most recently used)
      this.accessOrder = this.accessOrder.filter(k => k !== key);
      this.accessOrder.push(key);
      return this.cache.get(key);
    }
    return null;
  }

  set(key, value) {
    if (this.cache.has(key)) {
      this.cache.set(key, value);
      this.accessOrder = this.accessOrder.filter(k => k !== key);
      this.accessOrder.push(key);
    } else {
      if (this.cache.size >= this.maxSize) {
        // Remove least recently used
        const lru = this.accessOrder.shift();
        this.cache.delete(lru);
      }
      this.cache.set(key, value);
      this.accessOrder.push(key);
    }
  }

  clear() {
    this.cache.clear();
    this.accessOrder = [];
  }

  size() {
    return this.cache.size;
  }
}

// Battery-aware features
export const getBatteryInfo = async () => {
  if ('getBattery' in navigator) {
    try {
      const battery = await navigator.getBattery();
      return {
        level: battery.level,
        charging: battery.charging,
        chargingTime: battery.chargingTime,
        dischargingTime: battery.dischargingTime
      };
    } catch (error) {
      return null;
    }
  }
  return null;
};

export const isLowBattery = async () => {
  const battery = await getBatteryInfo();
  return battery && battery.level < 0.2 && !battery.charging;
};

// Optimize for low battery
export const optimizeForBattery = async (callback) => {
  const lowBattery = await isLowBattery();
  if (lowBattery) {
    callback();
  }
};

// Touch event optimization
export const optimizeTouch = (element, callback) => {
  let touchStartTime = 0;
  let touchStartPos = { x: 0, y: 0 };
  
  element.addEventListener('touchstart', (e) => {
    touchStartTime = Date.now();
    const touch = e.touches[0];
    touchStartPos = { x: touch.clientX, y: touch.clientY };
  }, { passive: true });
  
  element.addEventListener('touchend', (e) => {
    const touchEndTime = Date.now();
    const touchDuration = touchEndTime - touchStartTime;
    
    if (touchDuration < 200) { // Quick tap
      callback(e);
    }
  }, { passive: true });
};

// Reduce motion for accessibility
export const respectsReducedMotion = () => {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
};

// Performance observer for monitoring
export const setupPerformanceMonitoring = () => {
  if ('PerformanceObserver' in window) {
    const observer = new PerformanceObserver((list) => {
      list.getEntries().forEach((entry) => {
        if (entry.entryType === 'largest-contentful-paint') {
          console.log('LCP:', entry.startTime);
        }
        if (entry.entryType === 'first-input') {
          console.log('FID:', entry.processingStart - entry.startTime);
        }
        if (entry.entryType === 'layout-shift') {
          console.log('CLS:', entry.value);
        }
      });
    });
    
    observer.observe({ entryTypes: ['largest-contentful-paint', 'first-input', 'layout-shift'] });
  }
};

// Critical CSS inlining
export const inlineCriticalCSS = (css) => {
  const style = document.createElement('style');
  style.textContent = css;
  document.head.appendChild(style);
};

// Compression helpers
export const compressData = (data) => {
  try {
    return JSON.stringify(data);
  } catch (error) {
    return data;
  }
};

export const decompressData = (data) => {
  try {
    return JSON.parse(data);
  } catch (error) {
    return data;
  }
};

// Service Worker messaging
export const sendMessageToSW = (message) => {
  if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
    navigator.serviceWorker.controller.postMessage(message);
  }
};

// Background sync registration
export const registerBackgroundSync = (tag) => {
  if ('serviceWorker' in navigator && 'sync' in window.ServiceWorkerRegistration.prototype) {
    navigator.serviceWorker.ready.then((registration) => {
      return registration.sync.register(tag);
    });
  }
};

// Web Vitals measurement
export const measureWebVitals = (callback) => {
  if ('PerformanceObserver' in window) {
    // Measure CLS
    let clsValue = 0;
    const clsObserver = new PerformanceObserver((list) => {
      list.getEntries().forEach((entry) => {
        if (!entry.hadRecentInput) {
          clsValue += entry.value;
        }
      });
      callback('CLS', clsValue);
    });
    clsObserver.observe({ entryTypes: ['layout-shift'] });

    // Measure LCP
    const lcpObserver = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      const lastEntry = entries[entries.length - 1];
      callback('LCP', lastEntry.startTime);
    });
    lcpObserver.observe({ entryTypes: ['largest-contentful-paint'] });

    // Measure FID
    const fidObserver = new PerformanceObserver((list) => {
      list.getEntries().forEach((entry) => {
        callback('FID', entry.processingStart - entry.startTime);
      });
    });
    fidObserver.observe({ entryTypes: ['first-input'] });
  }
};

// Export all utilities
export default {
  lazyLoad,
  createLazyImage,
  debounce,
  throttle,
  VirtualScroller,
  getNetworkType,
  isSlowNetwork,
  adaptiveLoad,
  preloadResource,
  addResourceHints,
  MemoryManager,
  getBatteryInfo,
  isLowBattery,
  optimizeForBattery,
  optimizeTouch,
  respectsReducedMotion,
  setupPerformanceMonitoring,
  inlineCriticalCSS,
  compressData,
  decompressData,
  sendMessageToSW,
  registerBackgroundSync,
  measureWebVitals
};